import React from "react";
import AnalogClock from "./AnalogClock";
import DigitalClock from "./DigitalClock";
import AlarmControl from "./AlarmControl";

export default function App() {
    const [currentTime, setCurrentTime] = React.useState(new Date());
    const [clockType, setClockType] = React.useState("digital"); // "digital" or "analog"
    const [alarmTime, setAlarmTime] = React.useState(null); // { hours, minutes }
    const [alarmSound, setAlarmSound] = React.useState("beep");
    const [isAlarmRinging, setIsAlarmRinging] = React.useState(false);
    const [isAlarmActive, setIsAlarmActive] = React.useState(false);
    const audioRef = React.useRef(null);
    const fullscreenRef = React.useRef(null);
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    // Update current time every second
    React.useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Track fullscreen state (with webkit fallback for Safari desktop)
    React.useEffect(() => {
        const handler = () =>
            setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
        document.addEventListener("fullscreenchange", handler);
        document.addEventListener("webkitfullscreenchange", handler);
        return () => {
            document.removeEventListener("fullscreenchange", handler);
            document.removeEventListener("webkitfullscreenchange", handler);
        };
    }, []);

    function toggleFullscreen() {
        const el = fullscreenRef.current;
        const active = document.fullscreenElement || document.webkitFullscreenElement;
        if (active) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        } else if (el) {
            (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
        }
    }

    // Toggle palette must invert on the dark fullscreen background: otherwise the
    // selected (dark) button blends in while the unselected (light) one looks active.
    function clockToggleClass(selected) {
        const base =
            "cursor-pointer active:bg-slate-600 active:text-white focus:ring focus:ring-slate-300 w-1/2 rounded-lg px-2 sm:px-4 py-2 sm:py-4";
        const variant = isFullscreen
            ? selected
                ? "bg-white text-slate-900 shadow-md"
                : "bg-white/10 text-white hover:bg-white/20"
            : selected
                ? "bg-slate-800 hover:bg-slate-700 text-white shadow-md"
                : "bg-stone-100 hover:bg-stone-50 text-gray-900";
        return `${variant} ${base}`;
    }

    // Check if alarm should ring
    React.useEffect(() => {
        if (!isAlarmActive || !alarmTime) return;

        const currentHours = currentTime.getHours();
        const currentMinutes = currentTime.getMinutes();
        const currentSeconds = currentTime.getSeconds();

        if (
            currentHours === alarmTime.hours &&
            currentMinutes === alarmTime.minutes &&
            currentSeconds === 0
        ) {
            triggerAlarm();
        }
    }, [currentTime, alarmTime, isAlarmActive]);

    function triggerAlarm() {
        setIsAlarmRinging(true);
        playAlarmSound();
    }

    function playAlarmSound() {
        if (audioRef.current) {
            audioRef.current.play().catch((error) => {
                console.error("Error playing alarm sound:", error);
            });
        }
    }

    function stopAlarm() {
        setIsAlarmRinging(false);
        setIsAlarmActive(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }

    function snoozeAlarm() {
        setIsAlarmRinging(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        // Set alarm for 5 minutes later
        const snoozeTime = new Date();
        snoozeTime.setMinutes(snoozeTime.getMinutes() + 5);
        setAlarmTime({
            hours: snoozeTime.getHours(),
            minutes: snoozeTime.getMinutes(),
        });
    }

    function handleSetAlarm(hours, minutes, sound) {
        setAlarmTime({ hours, minutes });
        setAlarmSound(sound);
        setIsAlarmActive(true);
    }

    function handleCancelAlarm() {
        setAlarmTime(null);
        setIsAlarmActive(false);
        setIsAlarmRinging(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }

    const alarmSounds = {
        classic: 'https://public-jobmed.s3.eu-west-3.amazonaws.com/sfx/classic_alarm.mp3',
        tick: 'https://public-jobmed.s3.eu-west-3.amazonaws.com/sfx/tick-tock.mp3',
        telephone: 'https://public-jobmed.s3.eu-west-3.amazonaws.com/sfx/vintage-telephone.mp3',
        flute: 'https://public-jobmed.s3.eu-west-3.amazonaws.com/sfx/melodical-flute-2310.mp3',
        orchestra: 'https://public-jobmed.s3.eu-west-3.amazonaws.com/sfx/epic-orchestra.mp3'
    };

    return (
        <>
            <audio ref={audioRef} src={alarmSounds[alarmSound]} loop />

            <div
                ref={fullscreenRef}
                className={isFullscreen ? "bg-slate-900 w-full h-full flex flex-col justify-center items-center gap-6 p-4" : ""}
            >
            {/* Clock Type Toggle */}
            <div className={`flex text-center gap-2 sm:gap-4 ${isFullscreen ? "w-full max-w-md" : "w-full"}`}>
                <button
                    onClick={() => setClockType("digital")}
                    className={clockToggleClass(clockType === "digital")}
                    aria-label={`${clockType === "digital" ? "Sélectionné: " : ""}Horloge numérique`}
                    aria-pressed={clockType === "digital"}
                    role="button"
                >
                    <div className="">
                        <svg class="w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none">
                            <path d="M5 19.0001V21.0001M19 19.0001V21.0001" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M16 6H8C5.17157 6 3.75736 6 2.87868 6.87868C2 7.75736 2 9.17157 2 12V13C2 15.8284 2 17.2426 2.87868 18.1213C3.75736 19 5.17157 19 8 19H16C18.8284 19 20.2426 19 21.1213 18.1213C22 17.2426 22 15.8284 22 13V12C22 9.17157 22 7.75736 21.1213 6.87868C20.2426 6 18.8284 6 16 6Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M7 6L7.0883 5.73509C7.52832 4.41505 7.74832 3.75503 8.2721 3.37752C8.79587 3 9.49159 3 10.883 3H13.117C14.5084 3 15.2041 3 15.7279 3.37752C16.2517 3.75503 16.4717 4.41505 16.9117 5.73509L17 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M19 13.75V11.25C19 10.5597 18.4404 10 17.75 10C17.0596 10 16.5 10.5597 16.5 11.25V13.75C16.5 14.4404 17.0596 15 17.75 15C18.4404 15 19 14.4404 19 13.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M13.5 13.75V11.25C13.5 10.5596 12.9404 10 12.25 10C11.5596 10 11 10.5596 11 11.25V13.75C11 14.4404 11.5596 15 12.25 15C12.9404 15 13.5 14.4403 13.5 13.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M5 15V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M7.99996 11H8.00894M7.99102 14H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                        <div className="text-xs sm:text-sm font-semibold">Numérique</div>
                    </div>
                </button>
                <button
                    onClick={() => setClockType("analog")}
                    className={clockToggleClass(clockType === "analog")}
                    aria-label={`${clockType === "analog" ? "Sélectionné: " : ""}Horloge analogique`}
                    aria-pressed={clockType === "analog"}
                    role="button"
                >
                    <div>
                        <svg class="w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"></circle>
                            <path d="M9.5 9.5L12.9999 12.9996M16 8L11 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                        <div className="text-xs sm:text-sm font-semibold">Analogique</div>
                    </div>
                </button>
            </div>

            {/* Clock Display */}
            <div className={isFullscreen ? "w-full flex justify-center" : "bg-stone-100 rounded-lg p-6 sm:p-8 my-2 sm:my-4"}>
                {clockType === "digital" ? (
                    <DigitalClock time={currentTime} fullscreen={isFullscreen} />
                ) : (
                    <AnalogClock time={currentTime} fullscreen={isFullscreen} />
                )}
            </div>

            {/* Fullscreen toggle */}
            <button
                onClick={toggleFullscreen}
                className={
                    isFullscreen
                        ? "flex items-center justify-center gap-2 rounded-lg font-semibold text-sm py-2 px-4 bg-white/10 text-white hover:bg-white/20"
                        : "flex items-center justify-center gap-2 w-full rounded-lg font-semibold text-sm py-2 px-4 bg-stone-100 hover:bg-stone-50 text-gray-900 my-2 sm:my-4"
                }
                aria-label={isFullscreen ? "Quitter le plein écran" : "Afficher l'horloge en plein écran"}
            >
                {isFullscreen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                )}
                {isFullscreen ? "Quitter le plein écran" : "Plein écran"}
            </button>
            </div>

            {/* Alarm Ringing Alert */}
            {isAlarmRinging && (
                <div className="bg-red-200 text-white rounded-lg p-4 my-2 sm:my-4 animate-pulse">
                    <div className="text-center mb-4">
                        <div className="text-2xl font-bold mb-2">Réveil !</div>
                        <div className="text-lg">
                            {String(alarmTime.hours).padStart(2, "0")}:
                            {String(alarmTime.minutes).padStart(2, "0")}
                        </div>
                    </div>
                    <div className="flex gap-2 justify-center">
                        <button
                            onClick={stopAlarm}
                            className="bg-white text-red-500 px-6 py-2 rounded-lg font-semibold hover:bg-red-50"
                        >
                            Arrêter
                        </button>
                        <button
                            onClick={snoozeAlarm}
                            className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700"
                        >
                            Répéter (5 min)
                        </button>
                    </div>
                </div>
            )}

            {/* Alarm Control */}
            <AlarmControl
                onSetAlarm={handleSetAlarm}
                onCancelAlarm={handleCancelAlarm}
                currentAlarm={alarmTime}
                isActive={isAlarmActive}
                currentSound={alarmSound}
            />
        </>
    );
}
